// Moving brush entities: func_bobbing, func_pendulum, func_rotating,
// func_train, func_door, func_door_rotating, func_plat and func_button, ported from ET's
// game/g_mover.c (spawn parameters, SetMoverState / Reached_BinaryMover state
// machine, G_MoverPush / G_TryPushingEntity) and bg_misc.c (trajectories).
//
// Time model: ET moves movers in steps, once per server frame (sv_fps 20 =
// 50 ms); during pmove a mover stands where the last frame put it and riders
// are carried by G_MoverPush only at frame boundaries. We reproduce exactly
// that, per player and on the player's command clock: before a command whose
// serverTime crosses a frame boundary the session runs the frame (move the
// movers, push / carry its player, think and reached functions), then pmove.
// Client prediction, the server, replays and ghosts therefore all agree. Each
// player has its own mover phase and door/plat/button state (players do not
// collide with each other, so this does not change gameplay).

import { M_PI_F, f, fangleVectors, fdot, fnormalize, fvectoangles } from '../f32';
import type { Entity } from '../entities';
import { allowTeams, entityAngles, entityNum, entityVec, inlineModel } from '../entities';
import { ANGLE2SHORT, type Vec3 } from '../math';
import { EF_PRONE, playerBounds, type PlayerState } from '../physics/pmove';
import { MASK_PLAYERSOLID } from '../surfaceflags';
import { radiusFromBounds, type PhysicsWorld } from '../world';

// --- trajectories (bg_misc.c BG_EvaluateTrajectory) ---

export type TrType = 'stationary' | 'linear' | 'sine' | 'linearStop';

export interface Trajectory {
  type: TrType;
  /** Start time (ms). */
  time: number;
  /** Duration (ms) for sine and linearStop. */
  duration: number;
  base: Vec3;
  delta: Vec3;
}

export const stationary = (base: Vec3): Trajectory => ({ type: 'stationary', time: 0, duration: 0, base: [...base], delta: [0, 0, 0] });

/** BG_EvaluateTrajectory (float arithmetic like the C code). */
export function evaluateTrajectory(tr: Trajectory, atTime: number): Vec3 {
  const ma = (s: number): Vec3 => [f(tr.base[0] + f(tr.delta[0] * s)), f(tr.base[1] + f(tr.delta[1] * s)), f(tr.base[2] + f(tr.delta[2] * s))];
  switch (tr.type) {
    case 'linear':
      return ma(f((atTime - tr.time) * 0.001));
    case 'sine': {
      const deltaTime = f((atTime - tr.time) / f(tr.duration));
      return ma(f(Math.sin(f(f(deltaTime * M_PI_F) * 2))));
    }
    case 'linearStop': {
      if (atTime > tr.time + tr.duration) atTime = tr.time + tr.duration;
      let deltaTime = f((atTime - tr.time) * 0.001);
      if (deltaTime < 0) deltaTime = 0;
      return ma(deltaTime);
    }
    default:
      return [tr.base[0], tr.base[1], tr.base[2]];
  }
}

/** BG_EvaluateTrajectoryDelta: velocity (units or degrees per second). */
export function evaluateTrajectoryDelta(tr: Trajectory, atTime: number): Vec3 {
  switch (tr.type) {
    case 'linear':
      return [tr.delta[0], tr.delta[1], tr.delta[2]];
    case 'sine': {
      const deltaTime = f((atTime - tr.time) / f(tr.duration));
      const phase = f(f(Math.cos(f(f(deltaTime * M_PI_F) * 2))) * 0.5);
      return [f(tr.delta[0] * phase), f(tr.delta[1] * phase), f(tr.delta[2] * phase)];
    }
    case 'linearStop':
      if (atTime > tr.time + tr.duration || atTime < tr.time) return [0, 0, 0];
      return [tr.delta[0], tr.delta[1], tr.delta[2]];
    default:
      return [0, 0, 0];
  }
}

// --- static description (built once per map) ---

export type MoverKind = 'bobbing' | 'pendulum' | 'rotating' | 'train' | 'door' | 'doorRotating' | 'plat' | 'button';

export const MOVER_CLASSES: Record<string, MoverKind> = {
  func_bobbing: 'bobbing',
  func_pendulum: 'pendulum',
  func_rotating: 'rotating',
  func_train: 'train',
  func_door: 'door',
  func_door_rotating: 'doorRotating',
  func_plat: 'plat',
  func_button: 'button',
};

/** func_door_rotating spawnflags. */
const DOOR_ROTATING_FORCE = 32;
const DOOR_ROTATING_STAYOPEN = 64;

export interface TrainCorner {
  entityNum: number;
  origin: Vec3;
  speed: number;
  /** Seconds (-1 = stop here once). */
  wait: number;
  /** Next corner entity, -1 at the end of an open path. */
  next: number;
}

export interface MoverDef {
  entityNum: number;
  kind: MoverKind;
  model: number;
  spawnflags: number;
  /** Inline model bounds (CM_ModelBounds, spread by a unit). */
  mins: Vec3;
  maxs: Vec3;
  origin: Vec3;
  /** Spawn angles (pendulum base angles). */
  angles: Vec3;
  speed: number;
  closeSpeed: number;
  /** Milliseconds; -1000 = never return. */
  wait: number;
  damage: number;
  pos1: Vec3;
  pos2: Vec3;
  /** Bobbing / pendulum. */
  height: number;
  phase: number;
  toggle: boolean;
  /** Pendulum: swing period (ms). */
  period: number;
  hasTargetname: boolean;
  /** Has "health": shot open (ET), never touch opened. */
  shootable: boolean;
  /** Doors: "key" -1 (or targeted by a trigger without a key set): +activate can not open it. */
  locked: boolean;
  /** allowteams bits (1 << team) of the teams that may use it; 0 = every team. */
  teams: number;
  /** Rotating doors: the rotation axis (pitch / yaw / roll unit vector) and the opening angle in degrees. */
  rotate: Vec3;
  degrees: number;
  /** Trains: first corner entity and the corners by entity number. */
  trainStart: number;
  corners: Map<number, TrainCorner>;
}

/** G_SetMovedir: "angle" -1 = up, -2 = down, otherwise the forward vector. */
function moveDir(angles: Vec3): Vec3 {
  if (angles[0] === 0 && angles[1] === -1 && angles[2] === 0) return [0, 0, 1];
  if (angles[0] === 0 && angles[1] === -2 && angles[2] === 0) return [0, 0, -1];
  const fwd: Vec3 = [0, 0, 0];
  fangleVectors(angles, fwd, null, null);
  return fwd;
}

/** pos1 + movedir * (|movedir| . size - lip): the open position of doors and buttons. */
function movedPosition(pos1: Vec3, angles: Vec3, mins: Vec3, maxs: Vec3, lip: number): Vec3 {
  const dir = moveDir(angles);
  const size: Vec3 = [f(maxs[0] - mins[0]), f(maxs[1] - mins[1]), f(maxs[2] - mins[2])];
  const distance = f(fdot([Math.abs(dir[0]), Math.abs(dir[1]), Math.abs(dir[2])], size) - lip);
  return [f(pos1[0] + f(dir[0] * distance)), f(pos1[1] + f(dir[1] * distance)), f(pos1[2] + f(dir[2] * distance))];
}

/**
 * finishSpawningKeyedMover: a door without a "key" is locked (-1) when
 * something other than a trigger_aidoor or func_invisible_user targets it.
 */
function doorLocked(e: Entity, entities: Entity[]): boolean {
  if (e.key !== undefined && e.key !== '') return entityNum(e, 'key') < 0;
  const name = e.targetname;
  if (!name) return false;
  return entities.some((t) => t.target === name && !['trigger_aidoor', 'func_invisible_user'].includes((t.classname ?? '').toLowerCase()));
}

/** The spawn functions (SP_func_*) as data. */
export function createMoverDef(e: Entity, entNum: number, world: PhysicsWorld, entities: Entity[], byTargetname: Map<string, number[]>, gravity: number): MoverDef | null {
  const kind = MOVER_CLASSES[(e.classname ?? '').toLowerCase()];
  const model = inlineModel(e);
  const cm = world.cm.models[model];
  if (!kind || model <= 0 || !cm) return null;
  const origin = e.origin ? entityVec(e, 'origin') : ([0, 0, 0] as Vec3);
  const has = (k: string) => e[k] !== undefined && e[k] !== '';
  const def: MoverDef = {
    entityNum: entNum,
    kind,
    model,
    spawnflags: entityNum(e, 'spawnflags'),
    mins: [...cm.mins],
    maxs: [...cm.maxs],
    origin,
    angles: entityAngles(e),
    speed: entityNum(e, 'speed'),
    closeSpeed: entityNum(e, 'closespeed'),
    wait: entityNum(e, 'wait'),
    damage: entityNum(e, 'dmg'),
    pos1: [...origin],
    pos2: [...origin],
    height: 0,
    phase: entityNum(e, 'phase'),
    toggle: false,
    period: 0,
    hasTargetname: !!e.targetname,
    shootable: entityNum(e, 'health') !== 0,
    locked: false,
    teams: allowTeams(e),
    rotate: [0, 0, 0],
    degrees: 0,
    trainStart: -1,
    corners: new Map(),
  };
  switch (kind) {
    case 'bobbing':
      def.speed = entityNum(e, 'speed', 4);
      def.height = entityNum(e, 'height', 32);
      def.damage = entityNum(e, 'dmg', 2);
      break;
    case 'pendulum': {
      def.speed = entityNum(e, 'speed', 30);
      def.damage = entityNum(e, 'dmg', 2);
      // the frequency is a physical constant of the beam length
      const length = Math.max(8, Math.abs(def.mins[2]));
      const freq = f(f(1 / f(M_PI_F * 2)) * Math.sqrt(gravity / (3 * length)));
      def.period = Math.trunc(1000 / freq);
      break;
    }
    case 'rotating':
      if (!def.speed) def.speed = 100;
      if (!def.damage) def.damage = 2;
      break;
    case 'door': {
      if (!def.speed) def.speed = 400;
      if (!def.wait) def.wait = 2;
      def.wait = f(def.wait * 1000);
      def.damage = entityNum(e, 'dmg', 2);
      const lip = entityNum(e, 'lip', 8);
      def.pos2 = movedPosition(def.pos1, def.angles, def.mins, def.maxs, lip);
      if (def.spawnflags & 1) {
        // START_OPEN: reverse position 1 and 2 (and the speeds)
        [def.pos1, def.pos2] = [def.pos2, def.pos1];
        if (def.closeSpeed) [def.speed, def.closeSpeed] = [def.closeSpeed, def.speed];
      }
      def.toggle = (def.spawnflags & 2) !== 0;
      def.locked = doorLocked(e, entities);
      break;
    }
    case 'doorRotating': {
      // "time" (ms to open) and "speed" are the same field
      def.speed = has('time') ? entityNum(e, 'time') : def.speed;
      if (!def.speed) def.speed = 1000;
      def.degrees = entityNum(e, 'degrees') || 90;
      if (def.spawnflags & 16) def.degrees = -def.degrees; // REVERSE
      def.toggle = (def.spawnflags & 2) !== 0;
      def.rotate = def.spawnflags & 4 ? [0, 0, 1] : def.spawnflags & 8 ? [1, 0, 0] : [0, 1, 0];
      if (!def.wait) def.wait = 2;
      def.wait = f(def.wait * 1000);
      def.locked = doorLocked(e, entities);
      break;
    }
    case 'plat': {
      def.speed = entityNum(e, 'speed', 200);
      def.damage = entityNum(e, 'dmg', 2);
      def.wait = 1000;
      const lip = entityNum(e, 'lip', 8);
      const height = has('height') ? entityNum(e, 'height') : f(f(def.maxs[2] - def.mins[2]) - lip);
      // pos1 is the rest (bottom) position, pos2 is the top
      def.pos2 = [...origin];
      def.pos1 = [origin[0], origin[1], f(origin[2] - height)];
      break;
    }
    case 'button': {
      if (!def.speed) def.speed = 40;
      if (!def.wait) def.wait = 1;
      def.wait = f(def.wait * 1000);
      def.pos2 = movedPosition(def.pos1, def.angles, def.mins, def.maxs, entityNum(e, 'lip', 4));
      break;
    }
    case 'train': {
      if (!def.speed) def.speed = 100;
      def.damage = def.spawnflags & 4 ? 0 : def.damage || 2;
      def.toggle = (def.spawnflags & 2) !== 0;
      def.pos1 = [0, 0, 0];
      def.pos2 = [0, 0, 0];
      // Think_SetupTrainTargets: link the path_corner chain
      const first = e.target ? byTargetname.get(e.target)?.[0] : undefined;
      if (first === undefined) return null;
      def.trainStart = first;
      const corner = (n: number): TrainCorner => {
        let c = def.corners.get(n);
        if (!c) {
          const ce = entities[n];
          c = { entityNum: n, origin: entityVec(ce, 'origin'), speed: entityNum(ce, 'speed'), wait: entityNum(ce, 'wait'), next: -1 };
          def.corners.set(n, c);
        }
        return c;
      };
      let path = corner(first);
      while (path.next < 0) {
        const target = entities[path.entityNum].target;
        if (!target) break;
        const next = (byTargetname.get(target) ?? []).find((i) => (entities[i].classname ?? '').toLowerCase() === 'path_corner');
        if (next === undefined) break;
        path.next = next;
        path = corner(next);
      }
      break;
    }
  }
  return def;
}

// --- per player state ---

type BinaryState = 'pos1' | 'pos2' | '1to2' | '2to1';
type Think = 'returnToPos1' | 'beginMoving' | 'setupTrain' | null;

export interface MoverState {
  pos: Trajectory;
  apos: Trajectory;
  /** r.currentOrigin / r.currentAngles: the pose collision uses this frame. */
  origin: Vec3;
  angles: Vec3;
  state: BinaryState;
  think: Think;
  nextthink: number;
  duration: number;
  durationBack: number;
  pos1: Vec3;
  pos2: Vec3;
  /** Train: next path corner entity (-1 none). */
  nextTrain: number;
  /** Train: path corners whose "count" is set (wait -1 corners stop the train). */
  cornerCount: number[];
  /** Set in motion by another player (shared movers): its targets are theirs, not fired here. */
  external?: boolean;
  /** Doors and buttons used with +activate, until they are back at pos1 (ent->active). */
  active?: boolean;
  /** Rotating doors: the opening angle (degrees), its sign flipped to swing away from the player. */
  angle?: number;
}

export type MoverEventKind = 'use' | 'trigger' | 'impact';

/**
 * Something a player did to a mover (Use_BinaryMover from a target chain, the
 * door / plat trigger volume, touching a plat or button). In a room the
 * server hands these to every other player's movers at a frame of their own
 * clock, so all players see the same doors and platforms.
 */
export interface MoverEvent {
  /** Level time of the frame it happens at. */
  frame: number;
  entityNum: number;
  kind: MoverEventKind;
  /** Rotating doors: the opening angle after the use (it swings away from the player who opened it). */
  angle?: number;
}

/** Events of other players are kept this long after their frame (re-simulation after a correction). */
const EVENT_KEEP_MS = 5000;

/** Everything needed to restore a MoverSet (replays, prediction). */
export interface MoverSnapshot {
  time: number | null;
  states: MoverState[];
}

/** What the movers need from the player session. */
export interface MoverContext {
  /** The player (re-read after damage: dying respawns it). */
  readonly ps: PlayerState;
  readonly alive: boolean;
  /** The player's team (allowteams doors). */
  readonly team: 0 | 1;
  damage(amount: number): void;
  /** G_UseTargets of an entity (a mover or a path corner). */
  fireTargets(entityNum: number): void;
}

/** Longest stretch of frames simulated at once (a paused player resumes without a huge catch-up). */
const MAX_CATCHUP_MS = 30000;
const JITTER_INC = 4;

export class MoverSet {
  /** level.time of the last frame, null before the first command. */
  time: number | null = null;
  states: MoverState[];
  private readonly index = new Map<number, number>();
  /** Other players' mover events, applied when a frame reaches theirs (in frame order). */
  events: MoverEvent[] = [];
  /** This player's own actions that changed a mover (the server shares them). */
  onLocalEvent: ((e: MoverEvent) => void) | null = null;
  /** Events are kept from this frame on (the start of a timed run: its replay needs them). */
  keepEventsFrom: number | null = null;
  private applying = false;

  constructor(
    readonly defs: MoverDef[],
    private readonly world: PhysicsWorld,
    readonly frameMsec: number,
  ) {
    defs.forEach((d, i) => this.index.set(d.entityNum, i));
    this.states = defs.map((d) => this.initialState(d));
  }

  has(entityNum: number): boolean {
    return this.index.has(entityNum);
  }

  /** The spawn state (InitMover + the SP_func_* trajectories). */
  private initialState(d: MoverDef): MoverState {
    const st: MoverState = {
      pos: stationary(d.pos1),
      apos: stationary([0, 0, 0]),
      origin: [...d.pos1],
      angles: [0, 0, 0],
      state: 'pos1',
      think: null,
      nextthink: 0,
      duration: 1,
      durationBack: 1,
      pos1: [...d.pos1],
      pos2: [...d.pos2],
      nextTrain: -1,
      cornerCount: [],
    };
    // InitMover: time to reach the second position from the speed
    const move: Vec3 = [f(d.pos2[0] - d.pos1[0]), f(d.pos2[1] - d.pos1[1]), f(d.pos2[2] - d.pos1[2])];
    const distance = f(Math.sqrt(fdot(move, move)));
    const speed = d.speed || 100;
    st.duration = Math.max(1, Math.trunc(f(f(distance * 1000) / speed)));
    st.durationBack = d.closeSpeed ? Math.max(1, Math.trunc(f(f(distance * 1000) / d.closeSpeed))) : st.duration;
    switch (d.kind) {
      case 'bobbing': {
        const duration = Math.trunc(f(d.speed * 1000));
        const delta: Vec3 = [0, 0, 0];
        delta[d.spawnflags & 1 ? 0 : d.spawnflags & 2 ? 1 : 2] = d.height;
        st.pos = { type: 'sine', time: Math.trunc(f(duration * d.phase)), duration, base: [...d.origin], delta };
        st.origin = [...d.origin];
        break;
      }
      case 'pendulum':
        st.pos = stationary(d.origin);
        st.origin = [...d.origin];
        st.apos = { type: 'sine', time: Math.trunc(f(d.period * d.phase)), duration: d.period, base: [...d.angles], delta: [0, 0, d.speed] };
        st.angles = [...d.angles];
        break;
      case 'rotating': {
        const delta: Vec3 = [0, 0, 0];
        if (d.spawnflags & 1) delta[d.spawnflags & 4 ? 2 : d.spawnflags & 8 ? 0 : 1] = d.speed;
        st.pos = stationary(d.origin);
        st.origin = [...d.origin];
        st.apos = { type: 'linear', time: 0, duration: 0, base: [0, 0, 0], delta };
        break;
      }
      case 'train':
        st.cornerCount = [...d.corners.values()].filter((c) => c.wait === -1).map((c) => c.entityNum);
        break;
      case 'doorRotating':
        // InitMoverRotate: the "speed" is the time to open (ms)
        st.duration = st.durationBack = Math.max(1, Math.trunc(d.speed));
        st.angle = d.degrees;
        break;
    }
    return st;
  }

  /** Puts every mover's collision model where this player's movers are. */
  apply(): void {
    this.defs.forEach((d, i) => this.world.setPose(d.entityNum, this.states[i].origin, this.states[i].angles));
  }

  /** Pose for drawing at any time (trajectories evaluated, like cgame does). */
  pose(entityNum: number, time: number): { origin: Vec3; angles: Vec3 } | null {
    const i = this.index.get(entityNum);
    if (i === undefined) return null;
    const st = this.states[i];
    return { origin: evaluateTrajectory(st.pos, time), angles: evaluateTrajectory(st.apos, time) };
  }

  snapshot(): MoverSnapshot {
    return { time: this.time, states: structuredClone(this.states) };
  }

  restore(s: MoverSnapshot | null | undefined): void {
    if (!s || s.states.length !== this.defs.length) {
      this.time = null;
      this.states = this.defs.map((d) => this.initialState(d));
    } else {
      this.time = s.time;
      this.states = structuredClone(s.states);
    }
    this.apply();
  }

  /**
   * Runs the server frames up to the one containing `serverTime` (frame time
   * = floor(serverTime / frameMsec) * frameMsec). Call before each pmove.
   */
  advance(serverTime: number, ctx: MoverContext): void {
    const F = this.frameMsec;
    const frame = Math.floor(serverTime / F) * F;
    if (this.time === null) {
      // spawn: trains link their corners on the next frame
      this.time = frame;
      this.defs.forEach((d, i) => {
        if (d.kind === 'train') {
          this.states[i].think = 'setupTrain';
          this.states[i].nextthink = frame + F;
        }
      });
      this.apply();
      return;
    }
    // events of frames skipped by the catch-up limit come with the first frame run
    let last = this.time;
    if (frame - this.time > MAX_CATCHUP_MS) this.time = frame - MAX_CATCHUP_MS;
    while (this.time < frame) {
      this.time += F;
      this.runFrame(this.time, ctx);
      if (this.events.length) this.applyEvents(last, this.time, ctx);
      last = this.time;
    }
  }

  /** Other players' events of the frames after `from` up to t, after frame t ran (like their command did on their clock). */
  private applyEvents(from: number, t: number, ctx: MoverContext): void {
    for (const e of this.events) {
      if (e.frame <= from || e.frame > t) continue;
      const i = this.index.get(e.entityNum);
      if (i === undefined) continue;
      this.applying = true;
      try {
        this.states[i].external = true;
        if (e.kind === 'use') this.doUse(e.entityNum, ctx, e.angle);
        else if (e.kind === 'trigger') this.doTriggerTouched(e.entityNum, ctx);
        else this.doImpact(e.entityNum, ctx);
      } finally {
        this.applying = false;
      }
    }
    const keep = Math.min(t - EVENT_KEEP_MS, this.keepEventsFrom ?? Infinity);
    if (this.events[0] && this.events[0].frame < keep) this.events = this.events.filter((e) => e.frame >= keep);
  }

  /** Runs a player's own action on a mover; when it changed the mover, it is reported to be shared. */
  private local(kind: MoverEventKind, entityNum: number, run: () => void): void {
    const i = this.index.get(entityNum);
    if (i === undefined || this.time === null || this.applying) return run();
    const st = this.states[i];
    const key = (s: MoverState) => `${s.state}|${s.think}|${s.nextthink}|${s.pos.type}|${s.pos.time}|${s.apos.delta.join(',')}|${s.cornerCount.join(',')}|${s.angle}`;
    const before = key(st);
    run();
    if (key(st) === before) return;
    st.external = false;
    const e: MoverEvent = { frame: this.time, entityNum, kind };
    if (st.angle !== undefined) e.angle = st.angle;
    this.onLocalEvent?.(e);
  }

  /** G_RunFrame for the movers at level time `t`. */
  private runFrame(t: number, ctx: MoverContext): void {
    const prev = t - this.frameMsec;
    this.defs.forEach((d, i) => {
      const st = this.states[i];
      // G_RunMover: if stationary at one of the positions, don't move anything
      if (st.pos.type !== 'stationary' || st.apos.type !== 'stationary') this.moverTeam(d, st, t, prev, ctx);
      // G_RunThink
      if (st.nextthink > 0 && st.nextthink <= t) {
        st.nextthink = 0;
        this.think(d, st, t, ctx);
      }
    });
  }

  /** G_MoverTeam (teams are not supported: every mover is its own captain). */
  private moverTeam(d: MoverDef, st: MoverState, t: number, prev: number, ctx: MoverContext): void {
    const origin = evaluateTrajectory(st.pos, t);
    const angles = evaluateTrajectory(st.apos, t);
    const move: Vec3 = [f(origin[0] - st.origin[0]), f(origin[1] - st.origin[1]), f(origin[2] - st.origin[2])];
    const amove: Vec3 = [f(angles[0] - st.angles[0]), f(angles[1] - st.angles[1]), f(angles[2] - st.angles[2])];
    if (!this.moverPush(d, st, move, amove, ctx)) {
      // go back to the previous position
      st.pos.time += t - prev;
      st.apos.time += t - prev;
      st.origin = evaluateTrajectory(st.pos, t);
      st.angles = evaluateTrajectory(st.apos, t);
      this.world.setPose(d.entityNum, st.origin, st.angles);
      // the "blocked" function
      if (d.kind === 'door' || d.kind === 'plat' || d.kind === 'train') this.blockedDoor(d, st, t, true, ctx);
      else if (d.kind === 'doorRotating') this.blockedDoorRotate(d, st, t, true, ctx);
      return;
    }
    // the move succeeded: call the reached function if time is at or past end point
    if (st.pos.type === 'linearStop') {
      if (t >= st.pos.time + st.pos.duration) this.reached(d, st, t, ctx);
    } else if (st.apos.type === 'linearStop') {
      if (t >= st.apos.time + st.apos.duration) this.reached(d, st, t, ctx);
    }
  }

  /** G_TestEntityPosition for the player: the entity its box starts in, or -1. */
  private testPosition(ps: PlayerState): number {
    const { mins, maxs } = playerBounds(ps);
    const tr = this.world.trace(ps.origin, mins, maxs, ps.origin, MASK_PLAYERSOLID);
    return tr.startsolid ? tr.entityNum : -1;
  }

  /** G_MoverPush with the session's player as the only pushable entity. */
  private moverPush(d: MoverDef, st: MoverState, move: Vec3, amove: Vec3, ctx: MoverContext): boolean {
    // mins/maxs are the bounds at the destination
    const mins: Vec3 = [0, 0, 0];
    const maxs: Vec3 = [0, 0, 0];
    if (st.angles[0] || st.angles[1] || st.angles[2] || amove[0] || amove[1] || amove[2]) {
      const radius = radiusFromBounds(d.mins, d.maxs);
      for (let i = 0; i < 3; i++) {
        mins[i] = f(f(st.origin[i] - radius) + move[i]);
        maxs[i] = f(f(st.origin[i] + radius) + move[i]);
      }
    } else {
      // absmin / absmax as SV_LinkEntity sets them (one unit of epsilon)
      for (let i = 0; i < 3; i++) {
        mins[i] = f(f(f(st.origin[i] + d.mins[i]) - 1) + move[i]);
        maxs[i] = f(f(f(st.origin[i] + d.maxs[i]) + 1) + move[i]);
      }
    }
    // move the pusher to its final position
    for (let i = 0; i < 3; i++) {
      st.origin[i] = f(st.origin[i] + move[i]);
      st.angles[i] = f(st.angles[i] + amove[i]);
    }
    this.world.setPose(d.entityNum, st.origin, st.angles);

    let ps = ctx.ps;
    if (!ctx.alive || ps.pmType === 'noclip') return true;
    // if the player is standing on the pusher, it will definitely be moved
    if (ps.groundEntityNum !== d.entityNum) {
      const b = playerBounds(ps);
      for (let i = 0; i < 3; i++) {
        const amin = f(f(ps.origin[i] + b.mins[i]) - 1);
        const amax = f(f(ps.origin[i] + b.maxs[i]) + 1);
        if (amin >= maxs[i] || amax <= mins[i]) return true;
      }
      // is the player's box inside the pusher's final position?
      if (this.testPosition(ps) !== d.entityNum) return true;
    }
    // EF_MOVER_STOP (TRAIN_BLOCK_STOPS) stops on contact instead of pushing, riders are still carried
    if (d.kind === 'train' && d.spawnflags & 4 && ps.groundEntityNum !== d.entityNum) return false;
    const savedOrigin: Vec3 = [...ps.origin];
    const savedYaw = ps.deltaAngles[1];
    if (this.tryPushing(ps, st, move, amove)) return true;
    // bobbing entities are instant-kill and never get blocked
    if (st.pos.type === 'sine' || st.apos.type === 'sine') {
      ctx.damage(99999);
      return true;
    }
    // move back the player
    ps = ctx.ps;
    ps.origin = savedOrigin;
    ps.deltaAngles[1] = savedYaw;
    return false;
  }

  /** G_TryPushingEntity for the player: false if the move is blocked. */
  private tryPushing(ps: PlayerState, st: MoverState, move: Vec3, amove: Vec3): boolean {
    const oldOrigin: Vec3 = [...ps.origin];
    // make sure the view rotates with a rotating mover (only when prone)
    if (ps.eFlags & EF_PRONE) ps.deltaAngles[1] = (((ps.deltaAngles[1] + ANGLE2SHORT(amove[1]) + 32768) & 65535) - 32768) | 0;

    // figure movement due to the pusher's amove (BG_CreateRotationMatrix + transpose)
    const fwd: Vec3 = [0, 0, 0];
    const right: Vec3 = [0, 0, 0];
    const up: Vec3 = [0, 0, 0];
    fangleVectors(amove, fwd, right, up);
    const left: Vec3 = [-right[0], -right[1], -right[2]];
    const org: Vec3 = [f(ps.origin[0] - st.origin[0]), f(ps.origin[1] - st.origin[1]), f(ps.origin[2] - st.origin[2])];
    const org2: Vec3 = [fdot([fwd[0], left[0], up[0]], org), fdot([fwd[1], left[1], up[1]], org), fdot([fwd[2], left[2], up[2]], org)];
    const move2: Vec3 = [f(org2[0] - org[0]), f(org2[1] - org[1]), f(org2[2] - org[2])];
    for (let i = 0; i < 3; i++) ps.origin[i] = f(f(ps.origin[i] + move[i]) + move2[i]);
    if (this.testPosition(ps) < 0) return true;

    // if still not valid, move them around to see if we can find a good spot
    const jitterMax = ps.maxs[0] / 2.0;
    if (jitterMax > JITTER_INC) {
      const base: Vec3 = [...ps.origin];
      for (let z = 0; z < jitterMax; z += JITTER_INC) {
        for (let fz = -z; fz <= z; fz += 2 * z) {
          for (let x = JITTER_INC; x < jitterMax; x += JITTER_INC) {
            for (let fx = -x; fx <= x; fx += 2 * x) {
              for (let y = JITTER_INC; y < jitterMax; y += JITTER_INC) {
                for (let fy = -y; fy <= y; fy += 2 * y) {
                  ps.origin = [f(base[0] + fx), f(base[1] + fy), f(base[2] + fz)];
                  if (this.testPosition(ps) < 0) return true;
                }
              }
            }
          }
          if (!fz) break;
        }
      }
    }
    // if it is ok to leave in the old position, do it (riders left behind)
    ps.origin = oldOrigin;
    return this.testPosition(ps) < 0;
  }

  // --- binary movers (doors, plats, buttons) and trains ---

  /** SetMoverState */
  private setMoverState(d: MoverDef, st: MoverState, state: BinaryState, time: number, levelTime: number): void {
    st.state = state;
    st.pos.time = time;
    st.apos.time = time;
    if (state === 'pos1') st.active = false;
    if (d.kind === 'doorRotating') {
      this.setRotateState(d, st, state, levelTime);
      return;
    }
    const scaled = (a: Vec3, b: Vec3, fr: number): Vec3 => [f(f(a[0] - b[0]) * fr), f(f(a[1] - b[1]) * fr), f(f(a[2] - b[2]) * fr)];
    switch (state) {
      case 'pos1':
        st.pos.base = [...st.pos1];
        st.pos.type = 'stationary';
        break;
      case 'pos2':
        st.pos.base = [...st.pos2];
        st.pos.type = 'stationary';
        break;
      case '1to2':
        st.pos.base = [...st.pos1];
        st.pos.duration = st.duration;
        st.pos.delta = scaled(st.pos2, st.pos1, f(1000.0 / st.pos.duration));
        st.pos.type = 'linearStop';
        break;
      case '2to1':
        st.pos.base = [...st.pos2];
        st.pos.duration = d.closeSpeed ? st.durationBack : st.duration;
        st.pos.delta = scaled(st.pos1, st.pos2, f(1000.0 / st.pos.duration));
        st.pos.type = 'linearStop';
        break;
    }
    st.origin = evaluateTrajectory(st.pos, levelTime);
    this.world.setPose(d.entityNum, st.origin, st.angles);
  }

  /** SetMoverState for MOVER_POS1ROTATE .. MOVER_2TO1ROTATE (the angles move, the origin stays). */
  private setRotateState(d: MoverDef, st: MoverState, state: BinaryState, levelTime: number): void {
    const angle = st.angle ?? d.degrees;
    const apos = st.apos;
    switch (state) {
      case 'pos1':
      case 'pos2':
        apos.base = [...st.angles];
        apos.type = 'stationary';
        break;
      case '1to2': {
        // opening: from the closed angles {0,0,0}
        apos.base = [0, 0, 0];
        const fr = f(1000.0 / st.duration);
        apos.duration = st.duration;
        apos.delta = [f(d.rotate[0] * f(fr * angle)), f(d.rotate[1] * f(fr * angle)), f(d.rotate[2] * f(fr * angle))];
        apos.type = 'linearStop';
        break;
      }
      case '2to1': {
        // closing: from the open angles
        apos.base = [f(d.rotate[0] * angle), f(d.rotate[1] * angle), f(d.rotate[2] * angle)];
        const fr = f(1000.0 / st.duration);
        apos.duration = st.duration;
        apos.delta = [f(apos.base[0] * -fr), f(apos.base[1] * -fr), f(apos.base[2] * -fr)];
        apos.type = 'linearStop';
        st.active = false;
        break;
      }
    }
    st.origin = evaluateTrajectory(st.pos, levelTime);
    this.world.setPose(d.entityNum, st.origin, st.angles);
  }

  /** Blocked_DoorRotate: hurt the player, then reverse (rotating doors never crush). */
  private blockedDoorRotate(d: MoverDef, st: MoverState, t: number, byPlayer: boolean, ctx: MoverContext): void {
    if (byPlayer && d.damage) ctx.damage(d.damage);
    const time = t - (st.apos.duration - (t - st.apos.time));
    this.setMoverState(d, st, st.state === '1to2' ? '2to1' : '1to2', time, t);
  }

  /**
   * IsBinaryMoverBlocked: a rotating door opened towards the player would hit
   * it, so it swings the other way (not FORCE doors).
   */
  private swingsIntoPlayer(d: MoverDef, st: MoverState, ps: PlayerState): boolean {
    if (d.spawnflags & DOOR_ROTATING_FORCE) return false;
    const solid = this.world.solid(d.entityNum);
    if (!solid) return false;
    const pos: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) pos[i] = f(f(solid.absmin[i] + solid.absmax[i]) * 0.5);
    const dir: Vec3 = [f(pos[0] - d.origin[0]), f(pos[1] - d.origin[1]), f(pos[2] - d.origin[2])];
    const angles = fvectoangles(dir, [0, 0, 0]);
    const angle = st.angle ?? d.degrees;
    if (d.rotate[1]) angles[1] = f(angles[1] + angle);
    else if (d.rotate[0]) angles[0] = f(angles[0] + angle);
    else if (d.rotate[2]) angles[2] = f(angles[2] + angle);
    const forward: Vec3 = [0, 0, 0];
    fangleVectors(angles, forward, null, null);
    const vec: Vec3 = [f(ps.origin[0] - pos[0]), f(ps.origin[1] - pos[1]), f(ps.origin[2] - pos[2])];
    fnormalize(vec);
    return fdot(vec, forward) >= 0;
  }

  /** Blocked_Door (doors, plats and trains): hurt the blocker, then reverse. */
  private blockedDoor(d: MoverDef, st: MoverState, t: number, byPlayer: boolean, ctx: MoverContext): void {
    if (byPlayer && d.damage) ctx.damage(d.damage);
    if (d.spawnflags & 4) return; // crushers don't reverse (and blocked trains just stop)
    const time = t - (st.pos.duration - (t - st.pos.time));
    this.setMoverState(d, st, st.state === '1to2' ? '2to1' : '1to2', time, t);
  }

  private reached(d: MoverDef, st: MoverState, t: number, ctx: MoverContext): void {
    if (d.kind === 'train') {
      this.reachedTrain(d, st, t, ctx);
      return;
    }
    if (d.kind === 'doorRotating') {
      // STAYOPEN doors have no reached function: they stay open and can not be used again
      if (d.spawnflags & DOOR_ROTATING_STAYOPEN) return;
      if (st.state === '1to2') {
        this.setMoverState(d, st, 'pos2', t, t);
        if (!st.external) ctx.fireTargets(d.entityNum);
        // return to pos1 after a delay (toggle doors when used again)
        st.think = 'returnToPos1';
        st.nextthink = d.toggle ? 0 : t + d.wait;
      } else if (st.state === '2to1') {
        this.setMoverState(d, st, 'pos1', t, t);
      }
      return;
    }
    // Reached_BinaryMover
    if (st.state === '1to2') {
      this.setMoverState(d, st, 'pos2', t, t);
      if (!st.external) ctx.fireTargets(d.entityNum);
      if (d.toggle) {
        st.think = 'returnToPos1';
        st.nextthink = 0;
        return;
      }
      // return to pos1 after a delay
      if (d.wait !== -1000) {
        st.think = 'returnToPos1';
        st.nextthink = t + d.wait;
      }
    } else if (st.state === '2to1') {
      this.setMoverState(d, st, 'pos1', t, t);
    }
  }

  /** Reached_Train: head for the next path corner. */
  private reachedTrain(d: MoverDef, st: MoverState, t: number, ctx: MoverContext): void {
    const next = d.corners.get(st.nextTrain);
    if (!next || next.next < 0) return; // just stop
    if (next.wait === -1 && st.cornerCount.includes(next.entityNum)) return;
    // fire all other targets
    if (!st.external) ctx.fireTargets(next.entityNum);
    st.nextTrain = next.next;
    if (next.wait === -1 && !st.cornerCount.includes(next.entityNum)) st.cornerCount.push(next.entityNum);
    const after = d.corners.get(next.next)!;
    st.pos1 = [...next.origin];
    st.pos2 = [...after.origin];
    // if the path_corner has a speed, use that
    const speed = Math.max(1, next.speed || d.speed);
    const move: Vec3 = [f(st.pos2[0] - st.pos1[0]), f(st.pos2[1] - st.pos1[1]), f(st.pos2[2] - st.pos1[2])];
    const length = f(Math.sqrt(fdot(move, move)));
    st.duration = Math.trunc(f(f(length * 1000) / speed));
    this.setMoverState(d, st, '1to2', t, t);
    // if there is a "wait" value on the target, don't start moving yet
    if (next.wait) {
      st.think = 'beginMoving';
      st.nextthink = t + Math.trunc(f(next.wait * 1000));
      st.pos.type = 'stationary';
    }
  }

  private think(d: MoverDef, st: MoverState, t: number, ctx: MoverContext): void {
    const think = st.think;
    switch (think) {
      case 'returnToPos1':
        this.setMoverState(d, st, '2to1', t, t);
        break;
      case 'beginMoving':
        st.pos.time = t;
        st.pos.type = 'linearStop';
        break;
      case 'setupTrain':
        st.think = null;
        st.nextTrain = d.trainStart;
        if (d.toggle) {
          // TOGGLE trains wait at the first corner until used
          const c = d.corners.get(d.trainStart);
          if (c) {
            st.pos = stationary(c.origin);
            st.origin = [...c.origin];
            this.world.setPose(d.entityNum, st.origin, st.angles);
          }
        } else this.reachedTrain(d, st, t, ctx);
        break;
    }
  }

  /** Use_BinaryMover / Use_Func_Rotate / a train's use: fired by targets and triggers. */
  use(entityNum: number, ctx: MoverContext): void {
    this.local('use', entityNum, () => this.doUse(entityNum, ctx));
  }

  /**
   * +activate (Do_Activate_f) on a button or door that stands still and was
   * not activated already. Doors (G_TryDoor) also need to be unlocked and
   * allowed for the player's team; they fire their targets right away.
   * @returns whether the mover was used
   */
  activate(entityNum: number, ctx: MoverContext): boolean {
    const i = this.index.get(entityNum);
    if (i === undefined) return false;
    const d = this.defs[i];
    const st = this.states[i];
    if (st.pos.type !== 'stationary' || st.apos.type !== 'stationary' || st.active) return false;
    if (d.kind === 'button') {
      this.use(entityNum, ctx);
      st.active = true;
      return true;
    }
    if (d.kind !== 'door' && d.kind !== 'doorRotating') return false;
    if (d.locked || (d.teams && !(d.teams & (1 << ctx.team)))) return false;
    st.active = true;
    this.use(entityNum, ctx);
    ctx.fireTargets(entityNum);
    return true;
  }

  private doUse(entityNum: number, ctx: MoverContext, angle?: number): void {
    const i = this.index.get(entityNum);
    if (i === undefined || this.time === null) return;
    const d = this.defs[i];
    const st = this.states[i];
    const t = this.time;
    if (d.kind === 'doorRotating') {
      this.useDoorRotating(d, st, t, ctx, angle);
      return;
    }
    switch (d.kind) {
      case 'rotating': {
        const axis = d.spawnflags & 4 ? 2 : d.spawnflags & 8 ? 0 : 1;
        st.apos.delta[axis] = d.speed;
        return;
      }
      case 'train': {
        // a train stopped at a "wait -1" corner goes on (the next reached call passes it)
        const k = st.cornerCount.indexOf(st.nextTrain);
        if (k >= 0 && d.corners.get(st.nextTrain)?.wait === -1) st.cornerCount.splice(k, 1);
        // TOGGLE trains wait at the first corner until used
        else if (d.toggle && st.pos.type === 'stationary' && st.think === null) this.reachedTrain(d, st, t, ctx);
        return;
      }
      case 'bobbing':
      case 'pendulum':
        return;
    }
    // Use_BinaryMover
    switch (st.state) {
      case 'pos1':
        // start moving 50 msec later, because if this was player triggered,
        // level.time hasn't been advanced yet
        this.setMoverState(d, st, '1to2', t + 50, t);
        return;
      case 'pos2':
        // if all the way up, just delay before coming down
        if (d.toggle) st.nextthink = t + 50;
        else if (d.wait !== -1000) st.nextthink = t + d.wait;
        return;
      default:
        // only partway: reverse
        this.blockedDoor(d, st, t, false, ctx);
    }
  }

  /**
   * Use_BinaryMover of a rotating door. Closed: it opens away from the player
   * (or with the angle another player's use gave it). Open: the wait starts
   * again (toggle doors close). Moving: it turns back.
   */
  private useDoorRotating(d: MoverDef, st: MoverState, t: number, ctx: MoverContext, angle?: number): void {
    switch (st.state) {
      case 'pos1':
        if (angle !== undefined) st.angle = angle;
        else if (this.swingsIntoPlayer(d, st, ctx.ps)) st.angle = -(st.angle ?? d.degrees);
        // start moving 50 msec later, because if this was player triggered,
        // level.time hasn't been advanced yet
        this.setMoverState(d, st, '1to2', t + 50, t);
        return;
      case 'pos2':
        st.nextthink = d.toggle ? t + 50 : t + d.wait;
        return;
      default:
        this.blockedDoorRotate(d, st, t, false, ctx);
    }
  }

  /** Touch_DoorTrigger / Touch_PlatCenterTrigger (the spawned trigger volumes). */
  triggerTouched(entityNum: number, ctx: MoverContext): void {
    this.local('trigger', entityNum, () => this.doTriggerTouched(entityNum, ctx));
  }

  private doTriggerTouched(entityNum: number, ctx: MoverContext): void {
    const i = this.index.get(entityNum);
    if (i === undefined) return;
    const d = this.defs[i];
    const st = this.states[i];
    if (d.kind === 'door' && st.state !== '1to2') this.doUse(entityNum, ctx);
    else if (d.kind === 'plat' && st.state === 'pos1') this.doUse(entityNum, ctx);
  }

  /** ClientImpacts: the player's move ran into or stood on the mover (Touch_Plat, Touch_Button). */
  impact(entityNum: number, ctx: MoverContext): void {
    this.local('impact', entityNum, () => this.doImpact(entityNum, ctx));
  }

  private doImpact(entityNum: number, ctx: MoverContext): void {
    const i = this.index.get(entityNum);
    if (i === undefined || this.time === null) return;
    const d = this.defs[i];
    const st = this.states[i];
    if (d.kind === 'plat') {
      // delay return-to-pos1 by one second while a live player is on it
      if (ctx.alive && st.state === 'pos2') st.nextthink = this.time + 1000;
    } else if (d.kind === 'button' && d.spawnflags & 8) {
      if (st.state === 'pos1') this.doUse(entityNum, ctx);
    }
  }
}
